import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { CheckCircle, Lock } from 'lucide-react';
import useTableControls from '../../hooks/useTableControls';
import useAdminPageMeta from '../../hooks/useAdminPageMeta';
import { TableToolbar, TablePagination } from '../../components/admin/TableControls';

export default function UsersPage() {
  useAdminPageMeta('Users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/admin/users')
      .then(({ data }) => setUsers(data.users || data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const ctrl = useTableControls(users, {
    searchFn: (u, q) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.selected_avatar || '').toLowerCase().includes(q),
    filters: {
      access: (u, v) => (v === 'full' ? !!u.has_access : !u.has_access),
    },
    pageSize: 10,
  });

  return (
    <div className="p-4 sm:p-6 md:p-10">
      <div className="mb-6 md:mb-8">
        <h1 className="hero-title text-3xl md:text-4xl text-white">Users</h1>
        <p className="text-ee-text2">All readers on the platform.</p>
      </div>

      <TableToolbar
        query={ctrl.query}
        onQueryChange={ctrl.setQuery}
        placeholder="Search by name, email, avatar…"
        filters={[
          {
            key: 'access',
            label: 'Access',
            value: ctrl.filterValues.access,
            onChange: (v) => ctrl.setFilter('access', v),
            options: [
              { value: 'all', label: 'All access' },
              { value: 'full', label: 'Full access' },
              { value: 'locked', label: 'Locked' },
            ],
          },
        ]}
        pageSize={ctrl.pageSize}
        onPageSizeChange={ctrl.setPageSize}
      />

      <div className="ee-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-ee-bg2 text-ee-muted">
            <tr className="text-left">
              <th className="p-3 font-medium">Name</th>
              <th className="p-3 font-medium">Email</th>
              <th className="p-3 font-medium">Access</th>
              <th className="p-3 font-medium">Avatar</th>
              <th className="p-3 font-medium">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ee-border">
            {loading && <tr><td colSpan="5" className="p-6 text-center text-ee-muted">Loading…</td></tr>}
            {!loading && ctrl.filteredCount === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-ee-muted">
                {ctrl.total === 0 ? 'No users.' : 'No users match your filters.'}
              </td></tr>
            )}
            {ctrl.pageItems.map((u) => (
              <tr key={u.user_id} className="hover:bg-ee-card2">
                <td className="p-3 text-white">{u.name || '—'}</td>
                <td className="p-3 text-ee-text2">{u.email}</td>
                <td className="p-3">
                  {u.has_access
                    ? <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle size={12} /> Full</span>
                    : <span className="inline-flex items-center gap-1 text-ee-muted"><Lock size={12} /> Locked</span>}
                </td>
                <td className="p-3 text-ee-text2 capitalize">{u.selected_avatar || '—'}</td>
                <td className="p-3 text-ee-dim text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <TablePagination
          page={ctrl.page}
          pageCount={ctrl.pageCount}
          total={ctrl.total}
          filteredCount={ctrl.filteredCount}
          onPageChange={ctrl.setPage}
        />
      </div>
    </div>
  );
}
